<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="eingelangtModalTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="eingelangtModalTitle">Eingelangt</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Schließen"
            :disabled="submitting"
          ></button>
        </div>

        <div class="modal-body">
          <!-- Schritt 1: IMEI/SN bestätigen -->
          <template v-if="step === 'imei'">
            <p class="mb-2">Ist das die korrekte IMEI/SN Nummer?</p>
            <p class="imei-value">{{ imeiSn }}</p>

            <div class="form-check">
              <input id="imei-ok-yes" class="form-check-input" type="radio" :value="true" v-model="imeiOk" />
              <label class="form-check-label" for="imei-ok-yes">Ja</label>
            </div>
            <div class="form-check">
              <input id="imei-ok-no" class="form-check-input" type="radio" :value="false" v-model="imeiOk" />
              <label class="form-check-label" for="imei-ok-no">Nein</label>
            </div>

            <div v-if="imeiOk === false" class="mt-2">
              <label class="form-label" for="imei-reason">Begründung</label>
              <textarea id="imei-reason" v-model="imeiReason" class="form-control" rows="2"></textarea>
            </div>
          </template>

          <!-- Schritt 2: Akkutest -->
          <template v-else-if="step === 'battery'">
            <p class="mb-2">Akkutest möglich?</p>

            <div class="form-check">
              <input id="battery-yes" class="form-check-input" type="radio" value="capacity" v-model="batteryMode" />
              <label class="form-check-label" for="battery-yes">Ja, Kapazität (%)</label>
            </div>
            <div v-if="batteryMode === 'capacity'" class="mt-1 mb-2">
              <input
                v-model="batteryCapacity"
                type="number"
                min="0"
                max="100"
                step="0.1"
                class="form-control battery-capacity-input"
                placeholder="z. B. 80"
              />
            </div>

            <div class="form-check">
              <input id="battery-no" class="form-check-input" type="radio" value="reject" v-model="batteryMode" />
              <label class="form-check-label" for="battery-no">Nein, nicht möglich</label>
            </div>
            <div v-if="batteryMode === 'reject'" class="mt-1">
              <label class="form-label" for="battery-reason">Begründung</label>
              <textarea
                id="battery-reason"
                v-model="batteryReason"
                class="form-control"
                rows="2"
                placeholder="z. B. Akkutest nicht möglich da defekt"
              ></textarea>
            </div>
          </template>

          <!-- Schritt 3: Dateien (optional) -->
          <template v-else>
            <p class="mb-1 text-muted">
              Artikelfotos kannst du jetzt hinzufügen – oder den Wareneingang ohne
              Foto abschließen. (Andere Dokumente lädst du später auf der Detailseite hoch.)
            </p>
            <TicketFilesPanel :ticket-id="ticketId" :files="files" artikelfoto-only @changed="reloadFiles" />
          </template>

          <div v-if="stepError" class="error mt-2">{{ stepError }}</div>
          <div v-if="submitError" class="error mt-2">{{ submitError }}</div>
        </div>

        <div class="modal-footer">
          <button
            v-if="!isFirstStep"
            type="button"
            class="btn btn-secondary"
            :disabled="submitting"
            @click="onBack"
          >Zurück</button>
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="submitting">
            Abbrechen
          </button>
          <button
            v-if="step === 'imei'"
            type="button"
            class="btn btn-primary"
            @click="onImeiNext"
          >Weiter</button>
          <button
            v-else-if="step === 'battery'"
            type="button"
            class="btn btn-primary"
            @click="onBatteryNext"
          >Weiter</button>
          <button
            v-else
            type="button"
            class="btn btn-primary"
            :disabled="submitting"
            @click="onSubmit"
          >{{ submitting ? 'Speichert…' : 'Abschließen' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import TicketFilesPanel from './TicketFilesPanel.vue';
import {
  markEingelangt,
  get,
  type EingelangtPayload,
  type TicketDetail,
  type TicketFile,
} from '../services/tickets';

/**
 * Mehrstufiger Wizard für den Prozess „Eingelangt".
 * Aufruf via `open(ticket)`. Das Promise löst mit dem aktualisierten Ticket auf
 * (Erfolg / Datei-Änderung) oder mit `null` (abgebrochen ohne Änderung).
 *
 * Schritte: optional „imei" (wenn IMEI/SN vorhanden) → „battery" → „files" (optional).
 * Der Datei-Schritt nutzt dasselbe TicketFilesPanel wie die Detailseite (Upload mit
 * Kategorie, sofortiges Hochladen, Löschen mit Rückfrage). Eine Geräteauswahl gibt es
 * hier NICHT — ein Eingangsgerät ist für „Eingelangt" nicht erforderlich.
 */
type Step        = 'imei' | 'battery' | 'files';
type BatteryMode = 'capacity' | 'reject' | null;

const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;
let   resolver: ((result: TicketDetail | null) => void) | null = null;

const ticketId    = ref<number>(0);
const imeiSn      = ref<string>('');
const hasImeiStep = ref<boolean>(false);
const step        = ref<Step>('battery');

const files = ref<TicketFile[]>([]);

const imeiOk          = ref<boolean | null>(null);
const imeiReason      = ref<string>('');
const batteryMode     = ref<BatteryMode>(null);
const batteryCapacity = ref<string>('');
const batteryReason   = ref<string>('');

const stepError   = ref<string | null>(null);
const submitError = ref<string | null>(null);
const submitting  = ref<boolean>(false);

let result: TicketDetail | null = null;

/** Reihenfolge der aktiven Schritte für diese Ticket-Konstellation. */
const activeSteps = computed<Step[]>(() => {
  const steps: Step[] = [];
  if (hasImeiStep.value) steps.push('imei');
  steps.push('battery');
  steps.push('files');
  return steps;
});

const isFirstStep = computed(() => activeSteps.value.indexOf(step.value) <= 0);

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value, { backdrop: 'static', keyboard: false });
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

function open(ticket: TicketDetail): Promise<TicketDetail | null> {
  ticketId.value    = ticket.id;
  imeiSn.value      = ticket.imei_sn ?? '';
  hasImeiStep.value = !!ticket.imei_sn;
  files.value       = ticket.files ?? [];

  imeiOk.value          = null;
  imeiReason.value      = '';
  batteryMode.value     = null;
  batteryCapacity.value = '';
  batteryReason.value   = '';
  stepError.value       = null;
  submitError.value     = null;
  submitting.value      = false;
  result                = null;

  step.value = activeSteps.value[0];

  return new Promise<TicketDetail | null>(resolve => {
    resolver = resolve;
    modal?.show();
  });
}

function goToStep(target: Step): void {
  stepError.value = null;
  step.value      = target;
}

function nextStep(): void {
  const steps = activeSteps.value;
  const idx   = steps.indexOf(step.value);
  if (idx >= 0 && idx < steps.length - 1) goToStep(steps[idx + 1]);
}

function onBack(): void {
  const steps = activeSteps.value;
  const idx   = steps.indexOf(step.value);
  if (idx > 0) goToStep(steps[idx - 1]);
}

/** Nach Upload/Löschen im Panel das Ticket neu laden (Dateiliste + Ergebnis-Ticket). */
async function reloadFiles(): Promise<void> {
  try {
    const fresh = await get(ticketId.value);
    files.value = fresh.files;
    // Auch ohne „Abschließen" sollen hochgeladene/gelöschte Dateien auf der
    // Detailseite ankommen, wenn der Wizard danach geschlossen wird.
    result = fresh;
  } catch {
    /* Fehler werden im Panel angezeigt. */
  }
}

function onImeiNext(): void {
  if (imeiOk.value === null) {
    stepError.value = 'Bitte Ja oder Nein wählen.';
    return;
  }
  if (imeiOk.value === false && imeiReason.value.trim() === '') {
    stepError.value = 'Bitte eine Begründung angeben.';
    return;
  }
  nextStep();
}

/** Validiert die Akkutest-Eingabe; setzt stepError und gibt false zurück bei Fehler. */
function validateBattery(): boolean {
  if (batteryMode.value === null) {
    stepError.value = 'Bitte wählen, ob ein Akkutest möglich ist.';
    return false;
  }
  if (batteryMode.value === 'capacity') {
    const value = Number.parseFloat(batteryCapacity.value);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      stepError.value = 'Bitte eine Kapazität zwischen 0 und 100 eingeben.';
      return false;
    }
  } else if (batteryReason.value.trim() === '') {
    stepError.value = 'Bitte eine Begründung angeben.';
    return false;
  }
  stepError.value = null;
  return true;
}

function onBatteryNext(): void {
  if (validateBattery()) nextStep();
}

function buildPayload(): EingelangtPayload {
  const payload: EingelangtPayload = {};
  if (hasImeiStep.value) {
    payload.imei_verified = imeiOk.value === true;
    if (imeiOk.value === false) {
      payload.imei_not_verified_reason = imeiReason.value.trim();
    }
  }
  if (batteryMode.value === 'capacity') {
    payload.battery_test_capacity = Number.parseFloat(batteryCapacity.value);
  } else {
    payload.battery_test_reject_reasons = batteryReason.value.trim();
  }
  return payload;
}

async function onSubmit(): Promise<void> {
  if (submitting.value) return;
  if (!validateBattery()) {
    step.value = 'battery';
    return;
  }

  submitting.value  = true;
  submitError.value = null;
  try {
    // Dateien wurden im Datei-Schritt bereits hochgeladen; hier nur den
    // Wareneingang erfassen. Das zurückgelieferte Ticket enthält die Dateiliste.
    const antwort = await markEingelangt(ticketId.value, buildPayload());
    result        = antwort.ticket;
    modal?.hide();
  } catch (e) {
    submitError.value = e instanceof Error ? `Speichern fehlgeschlagen: ${e.message}` : 'Speichern fehlgeschlagen.';
    submitting.value  = false;
  }
}

function onHidden(): void {
  resolver?.(result);
  resolver = null;
}

defineExpose({ open });
</script>

<style scoped>
.imei-value {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 12px;
}

.battery-capacity-input {
  max-width: 160px;
}

</style>
